import type {
  SupplierAuthDTO,
  SupplierPriceDTO,
  SupplierProductDTO,
  SupplierSearchDTO,
  SupplierStockDTO,
} from '../dto'
import type { SupplierResponse } from '../entities'
import type { ApiClient } from '../api/ApiClient'
import type { IntegrationCache } from '../api/CacheStore'
import type { CircuitBreaker } from '../api/CircuitBreaker'
import type { RateLimiter } from '../api/RateLimiter'
import type { ISupplierAdapter } from './ISupplierAdapter'

export interface AdapterDeps {
  client: ApiClient
  cache: IntegrationCache
  rateLimiter: RateLimiter
  circuitBreaker: CircuitBreaker
  cacheTtlMs: number
}

/**
 * BaseSupplierAdapter — shared infrastructure behaviors for all vendors.
 */
export abstract class BaseSupplierAdapter implements ISupplierAdapter {
  abstract readonly supplierCode: string
  protected authenticated = false
  protected auth: SupplierAuthDTO | null = null
  protected readonly deps: AdapterDeps

  constructor(deps: AdapterDeps) {
    this.deps = deps
  }

  async authenticate(auth?: SupplierAuthDTO): Promise<SupplierResponse<boolean>> {
    return this.execute('authenticate', async () => {
      this.auth = auth ?? this.auth
      this.authenticated = Boolean(
        this.auth?.username || this.auth?.apiKey || this.auth?.token,
      )
      // Mock auth — no real API in Sprint 1.2
      await this.deps.client.request({
        method: 'POST',
        url: `/mock/${this.supplierCode}/auth`,
        body: { username: this.auth?.username ?? 'mock' },
      })
      return this.authenticated
    })
  }

  async refreshAuthentication(): Promise<SupplierResponse<boolean>> {
    return this.execute('refreshAuthentication', async () => {
      if (!this.auth) {
        this.authenticated = false
        return false
      }
      await this.deps.client.request({
        method: 'POST',
        url: `/mock/${this.supplierCode}/auth/refresh`,
        body: { refreshToken: this.auth.refreshToken ?? 'mock-refresh' },
      })
      this.authenticated = true
      return true
    })
  }

  async searchProducts(query: SupplierSearchDTO): Promise<SupplierResponse<SupplierProductDTO>> {
    const cacheKey = `search:${this.supplierCode}:${query.term}:${query.page ?? 1}`
    return this.execute('searchProducts', async () => {
      const cached = this.deps.cache.get<SupplierProductDTO[]>(cacheKey)
      if (cached) return cached
      await this.ensureAuth()
      const response = await this.deps.client.request<{ items: SupplierProductDTO[] }>({
        method: 'GET',
        url: `/mock/${this.supplierCode}/products`,
        query: { q: query.term, page: String(query.page ?? 1) },
      })
      const items =
        response.data?.items && response.data.items.length > 0
          ? response.data.items
          : this.mockProducts(query.term)
      this.deps.cache.set(cacheKey, items, this.deps.cacheTtlMs)
      return items
    })
  }

  async getProduct(id: string): Promise<SupplierResponse<SupplierProductDTO>> {
    const cacheKey = `product:${this.supplierCode}:${id}`
    return this.execute('getProduct', async () => {
      const cached = this.deps.cache.get<SupplierProductDTO[]>(cacheKey)
      if (cached) return cached
      await this.ensureAuth()
      await this.deps.client.request({
        method: 'GET',
        url: `/mock/${this.supplierCode}/products/${id}`,
      })
      const items = this.mockProducts(id).slice(0, 1)
      this.deps.cache.set(cacheKey, items, this.deps.cacheTtlMs)
      return items
    })
  }

  async getStock(productId: string): Promise<SupplierResponse<SupplierStockDTO>> {
    const cacheKey = `stock:${this.supplierCode}:${productId}`
    return this.execute('getStock', async () => {
      const cached = this.deps.cache.get<SupplierStockDTO[]>(cacheKey)
      if (cached) return cached
      await this.ensureAuth()
      await this.deps.client.request({
        method: 'GET',
        url: `/mock/${this.supplierCode}/stock/${productId}`,
      })
      const items: SupplierStockDTO[] = [
        {
          productId,
          sku: productId,
          quantity: 10 + Math.floor(Math.random() * 40),
          warehouse: 'DEFAULT',
          updatedAt: new Date().toISOString(),
        },
      ]
      this.deps.cache.set(cacheKey, items, this.deps.cacheTtlMs)
      return items
    })
  }

  async getPrice(productId: string): Promise<SupplierResponse<SupplierPriceDTO>> {
    const cacheKey = `price:${this.supplierCode}:${productId}`
    return this.execute('getPrice', async () => {
      const cached = this.deps.cache.get<SupplierPriceDTO[]>(cacheKey)
      if (cached) return cached
      await this.ensureAuth()
      await this.deps.client.request({
        method: 'GET',
        url: `/mock/${this.supplierCode}/price/${productId}`,
      })
      const purchase = 100 + Math.random() * 900
      const items: SupplierPriceDTO[] = [
        {
          productId,
          sku: productId,
          purchasePrice: round2(purchase),
          pixPrice: round2(purchase * 0.97),
          cardPrice: round2(purchase * 1.08),
          currency: 'BRL',
          updatedAt: new Date().toISOString(),
        },
      ]
      this.deps.cache.set(cacheKey, items, this.deps.cacheTtlMs)
      return items
    })
  }

  async healthCheck(): Promise<SupplierResponse<{ online: boolean; latency: number }>> {
    return this.execute('healthCheck', async () => {
      const started = performance.now()
      await this.deps.client.request({
        method: 'GET',
        url: `/mock/${this.supplierCode}/health`,
      })
      const latency = Math.round(performance.now() - started)
      return [{ online: true, latency }]
    })
  }

  async disconnect(): Promise<SupplierResponse<boolean>> {
    return this.execute('disconnect', async () => {
      this.authenticated = false
      this.auth = null
      return true
    })
  }

  protected async ensureAuth(): Promise<void> {
    if (!this.authenticated) {
      await this.authenticate(this.auth ?? { username: 'mock', password: 'mock' })
    }
  }

  protected mockProducts(term: string): SupplierProductDTO[] {
    return [
      {
        id: `${this.supplierCode}-${term}-1`,
        sku: `${this.supplierCode.toUpperCase()}-SKU-1`,
        description: `${term} (mock ${this.supplierCode})`,
        brand: 'Genérica',
        model: 'Mock',
        currency: 'BRL',
        purchasePrice: 499.9,
        pixPrice: 479.9,
        cardPrice: 529.9,
        stock: 25,
        productUrl: '#',
        imageUrl: '',
      },
    ]
  }

  protected async execute<T>(
    _operation: string,
    fn: () => Promise<T[] | T>,
  ): Promise<SupplierResponse<T extends Array<infer U> ? U : T>> {
    const requestId = crypto.randomUUID()
    const started = performance.now()
    const errors: string[] = []
    const warnings: string[] = []

    try {
      await this.deps.rateLimiter.acquire(this.supplierCode)
      this.deps.circuitBreaker.assertClosed(this.supplierCode)

      const result = await fn()
      const items = (Array.isArray(result) ? result : [result]) as Array<
        T extends Array<infer U> ? U : T
      >
      this.deps.circuitBreaker.recordSuccess(this.supplierCode)

      return {
        supplier: this.supplierCode,
        status: 'success',
        executionTime: Math.round(performance.now() - started),
        requestId,
        items,
        errors,
        warnings,
      }
    } catch (error) {
      this.deps.circuitBreaker.recordFailure(this.supplierCode)
      errors.push(error instanceof Error ? error.message : 'Erro desconhecido')
      return {
        supplier: this.supplierCode,
        status: 'error',
        executionTime: Math.round(performance.now() - started),
        requestId,
        items: [],
        errors,
        warnings,
      }
    }
  }
}

function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100
}
